<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import {FwbTable, FwbTableBody, FwbTableCell, FwbTableHead, FwbTableHeadCell, FwbTableRow} from "flowbite-vue";
import DownloadIcon from "@/Components/Common/Svg/DownloadIcon.vue";
import EditPencil from "@/Components/Common/Svg/EditPencil.vue";
import CheckIcon from "@/Components/Common/Svg/CheckIcon.vue";
import {Link} from "@inertiajs/inertia-vue3";
import DialogModal from "@/Components/DialogModal.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import {ref} from "vue";
import {Inertia} from "@inertiajs/inertia";

defineProps({
  fillOrders: Object
});

const cloningRecord = ref(null);
const markReceived = ref({marking: false, record: null});
const startMarkingReceived = (fillOrder) => {
  markReceived.value.record = fillOrder;
}

const stopMarkingReceived = () => {
  markReceived.value.record = null;
}

const markFillOrderReceived = (fillOrder) => {
  markReceived.value.marking = true;
  Inertia.post(route('admin.fill-orders.mark-received', {fill_order: fillOrder.oid}), {}, {
    onFinish() {
      markReceived.value.marking = false;
      markReceived.value.record = null;
    },
    preserveScroll: true,
  });
}

const markCancelled = ref({marking: false, record: null});
const startMarkingCancelled = (fillOrder) => {
  markCancelled.value.record = fillOrder;
}

const stopMarkingCancelled = () => {
  markCancelled.value.record = null;
}

const markFillOrderCancelled = (fillOrder) => {
  markCancelled.value.marking = true;
  Inertia.post(route('admin.fill-orders.mark-cancelled', {fill_order: fillOrder.oid}), {}, {
    onFinish() {
      markCancelled.value.marking = false;
      markCancelled.value.record = null;
    },
    preserveScroll: true,
  });
}

const cloneOrder = (fillOrder) => {
  cloningRecord.value = fillOrder;
  Inertia.post(route('admin.fill-orders.clone', {fill_order: fillOrder.oid}), {}, {
    onFinish() {
      cloningRecord.value = null
    },
    preserveScroll: true,
  });
}

const isReceived = (fillOrder) => {
  return fillOrder.received_at !== null;
}
const breadcrumb = {title: 'Incoming Fill Orders'};
if (route().current().includes('.received')) {
  breadcrumb.title = 'Received Fill Orders';
}
</script>

<template>
  <AppLayout :breadcrumbs="[breadcrumb]" :title="breadcrumb.title">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-900 leading-tight" v-text="breadcrumb.title"></h2>
    </template>
    <div class="mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div class="flex flex-row-reverse">
        <Link :href="route('admin.fill-orders.create')" v-if="route().current().includes('.incoming')" class="ml-4">
          <PrimaryButton>Add Fill Order</PrimaryButton>
        </Link>
        <Link :href="route('admin.fill-orders.received')" v-if="route().current().includes('.incoming')">
          <SecondaryButton>View Received Fill Orders</SecondaryButton>
        </Link>
        <Link :href="route('admin.fill-orders.incoming')" v-if="route().current().includes('.received')">
          <SecondaryButton>View Incoming Fill Orders</SecondaryButton>
        </Link>
      </div>
      <FwbTable striped class="border border-gray-200 sm:rounded-b-none mt-5 shadow-none">
        <FwbTableHead class="border-b border-gray-200">
          <FwbTableHeadCell>Vendor</FwbTableHeadCell>
          <FwbTableHeadCell>Company</FwbTableHeadCell>
          <FwbTableHeadCell>Project Name</FwbTableHeadCell>
          <FwbTableHeadCell>Purchase Order</FwbTableHeadCell>
          <FwbTableHeadCell>Date Placed</FwbTableHeadCell>
          <FwbTableHeadCell>Date Expected</FwbTableHeadCell>
          <FwbTableHeadCell>Download</FwbTableHeadCell>
          <FwbTableHeadCell>Edit</FwbTableHeadCell>
          <FwbTableHeadCell>Received</FwbTableHeadCell>
          <FwbTableHeadCell v-if="route().current().includes('.incoming')">Cancel</FwbTableHeadCell>
        </FwbTableHead>
        <FwbTableBody>
          <FwbTableRow v-for="fillOrder in fillOrders.data" class="border-b-gray-200 text-gray-700">
            <FwbTableCell v-text="fillOrder.vendor_record.vendorName"></FwbTableCell>
            <FwbTableCell v-text="fillOrder.company.client"></FwbTableCell>
            <FwbTableCell v-text="fillOrder.prj_name"></FwbTableCell>
            <FwbTableCell v-text="fillOrder.pu_order"></FwbTableCell>
            <FwbTableCell v-text="fillOrder.placed"></FwbTableCell>
            <FwbTableCell v-text="fillOrder.expected"></FwbTableCell>
            <FwbTableCell>
              <a v-if="fillOrder.media_thumbnail" target="_blank" :href="fillOrder.media_thumbnail.url"
                 class="cursor-pointer" :title="`Download ${fillOrder.media_thumbnail.file_name}`">
                <DownloadIcon></DownloadIcon>
              </a>
            </FwbTableCell>
            <FwbTableCell>
              <Link :href="route('admin.fill-orders.edit', {fill_order: fillOrder.oid})" :title="`Edit Fill Order`">
                <EditPencil></EditPencil>
              </Link>
            </FwbTableCell>
            <FwbTableCell class="text-center">
              <div class="inline-flex items-center w-full">
                <CheckIcon v-if="!isReceived(fillOrder)" class="cursor-pointer"
                           @click="startMarkingReceived(fillOrder)"></CheckIcon>
                <CheckIcon v-else color="green"></CheckIcon>
                <button v-if="isReceived(fillOrder)" type="button" @click="cloneOrder(fillOrder)"
                        :disabled="cloningRecord?.oid === fillOrder.oid"
                        class="ml-2 flex items-center justify-center px-3 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed">
                  New Order
                </button>
              </div>
            </FwbTableCell>
            <FwbTableCell v-if="route().current().includes('.incoming')">
              <span class="fa fa-trash text-gray-500 cursor-pointer" @click="startMarkingCancelled(fillOrder)"></span>
            </FwbTableCell>
          </FwbTableRow>
        </FwbTableBody>
      </FwbTable>
      <div aria-label="Table navigation">
        <PaginationLinks :pagination="fillOrders"/>
      </div>
    </div>

    <DialogModal :show="markReceived.record !== null" @close="stopMarkingReceived">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        <p class="text-gray-700">You are about to mark the Incoming Fill Order as Received<br>Are you sure?</p>
      </template>

      <template #footer>
        <SecondaryButton @click="stopMarkingReceived" class="mr-2" :disabled="markReceived.marking">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="markFillOrderReceived(markReceived.record)" :disabled="markReceived.marking">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="markCancelled.record !== null" @close="stopMarkingCancelled">
      <template #title>
        <b>Please confirm</b>
      </template>

      <template #content>
        <p class="text-gray-700">You are about to cancel this Fill Order<br>Are you sure?</p>
      </template>

      <template #footer>
        <SecondaryButton @click="stopMarkingCancelled" class="mr-2" :disabled="markCancelled.marking">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="markFillOrderCancelled(markCancelled.record)" :disabled="markCancelled.marking">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
